import React, { useEffect, useState, useCallback } from "react";
import { Outlet, useNavigate, useParams, useLocation } from "react-router-dom";
import { Button, Space, Tabs, Typography, Tag, Switch, message } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { readForm, updateForm } from "../../../redux/actions/formsAction";
import { FormDetailContext } from "./FormDetailContext";
import { isValidFormId, formDetailPath } from "./formUtils";

const { Title, Text } = Typography;

const TAB_SECTIONS = [
  "overview",
  "submissions",
  "settings",
  "fields",
  "dpdp",
  "lead-scoring",
  "integration",
];

function sectionFromPathname(pathname, formId) {
  if (formId === "new") return "settings";
  const prefix = `/app/forms/${formId}`;
  if (pathname === prefix || pathname === `${prefix}/`) return "overview";
  for (const section of TAB_SECTIONS) {
    if (pathname.endsWith(`/${section}`)) return section;
  }
  return "overview";
}

export default function FormDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const isNew = id === "new" || !isValidFormId(id);

  const [formRecord, setFormRecord] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [publicId, setPublicId] = useState("");

  const activeSection = isNew
    ? "settings"
    : sectionFromPathname(location.pathname, id);

  const reloadForm = useCallback(() => {
    if (!isValidFormId(id)) return Promise.resolve();
    setLoading(true);
    return dispatch(readForm(id))
      .then((data) => {
        setFormRecord(data);
        setPublicId(data.publicId);
        setLoading(false);
        return data;
      })
      .catch(() => {
        setLoading(false);
        return null;
      });
  }, [dispatch, id]);

  useEffect(() => {
    if (id === "undefined") {
      message.error("Invalid form link.");
      navigate("/app/forms", { replace: true });
      return;
    }
    if (isNew) {
      setFormRecord(null);
      setPublicId("");
      setLoading(false);
      if (location.pathname !== "/app/forms/new") {
        navigate("/app/forms/new", { replace: true });
      }
      return;
    }
    if (!isValidFormId(id)) {
      navigate("/app/forms", { replace: true });
      return;
    }
    reloadForm();
  }, [id, isNew, navigate, reloadForm, location.pathname]);

  useEffect(() => {
    if (isNew && activeSection !== "settings") {
      navigate("/app/forms/new", { replace: true });
    }
  }, [isNew, activeSection, navigate]);

  const onTabChange = (key) => {
    navigate(formDetailPath(id, key));
  };

  const tabItems = isNew
    ? [{ key: "settings", label: "Settings" }]
    : [
        { key: "overview", label: "Overview" },
        { key: "submissions", label: "Submissions" },
        { key: "settings", label: "Settings" },
        { key: "fields", label: "Fields" },
        { key: "dpdp", label: "Consent & DPDP" },
        { key: "lead-scoring", label: "Gate & Scoring" },
        { key: "integration", label: "Integration" },
      ];

  const contextValue = {
    id,
    isNew,
    formRecord,
    publicId,
    setPublicId,
    loading,
    reloadForm,
  };

  return (
    <FormDetailContext.Provider value={contextValue}>
      <div className="FormDetail">
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Space align="start" style={{ width: "100%", justifyContent: "space-between" }}>
            <Space align="start">
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate("/app/forms")}
              />
              <div>
                <Title level={5} style={{ margin: 0 }}>
                  {isNew ? "New form" : formRecord?.name || "Form"}
                </Title>
                {!isNew && formRecord && (
                  <Space style={{ marginTop: 8 }} wrap>
                    <Text type="secondary">ID: {formRecord.publicId}</Text>
                    <Tag>{formRecord.contactPromotion}</Tag>
                    <Tag color={formRecord.enabled ? "green" : "default"}>
                      {formRecord.enabled ? "Enabled" : "Disabled"}
                    </Tag>
                    <Switch
                      size="small"
                      checked={formRecord.enabled}
                      onChange={(checked) => {
                        dispatch(updateForm({ enabled: checked }, id)).then(() =>
                          reloadForm(),
                        );
                      }}
                    />
                  </Space>
                )}
              </div>
            </Space>
          </Space>

          <Tabs activeKey={activeSection} items={tabItems} onChange={onTabChange} />

          <Outlet />
        </Space>
      </div>
    </FormDetailContext.Provider>
  );
}
